/**
 * SEO Redirects — handles legacy octasker.com (WordPress) URL → new app URL.
 *
 * Why this lives in React (not Cloudflare/Emergent edge): the new app is a
 * single-page React build served from a single index.html. Any 404 path
 * lands here, so we do client-side 301-equivalent <Navigate replace> with
 * a prerendered <link rel="canonical"> in the served HTML so Google still
 * sees the redirect intent.
 *
 * Captured from `https://octasker.com/sitemap_index.xml` (April 2026 crawl)
 * before the GoDaddy DNS cutover. Sources: page-sitemap.xml (54),
 * post-sitemap.xml (22), category-sitemap.xml (13).
 *
 * Three rules:
 *   1. Trailing-slash → no trailing slash (`/about/` → `/about`).
 *   2. Exact path mappings (e.g., `/estimate/` → `/get-estimate`).
 *   3. Pattern mappings (e.g., legacy blog posts at root → `/blog/<slug>`).
 *
 * Anything not matched falls through to the app's existing <NotFound> route.
 */
import { useLocation, Navigate } from "react-router-dom";

// ---- 1. Exact mappings (legacy WordPress page → new app route) ----
const EXACT_REDIRECTS = {
  // Aug 2026 URL migration: /handyman-services/* → /services/*
  // Old service slugs that hop to a DIFFERENT destination stay in this
  // EXACT_REDIRECTS table — the /handyman-services/:slug route pattern
  // otherwise defaults to /services/:slug via HandymanServicesRedirect
  // (see App.js).
  //
  // Aug 14 2026 fix: `/services/handyman` was a soft 404 because the
  // umbrella "handyman" service is stored under slug `home-repair-services`.
  // The AI Local Visibility spec listed /services/handyman/ as a top-tier
  // URL, so external inbound links + Google Search Console URL-inspections
  // for this slug were landing on the ServiceDetailPage "not found"
  // fallback. Client-side redirect fixes the crawl error and consolidates
  // any inbound authority onto the real handyman umbrella page.
  "/services/handyman": "/services/home-repair-services",
  //
  // NOTE: home-maintenance-plans is NO LONGER redirected — as of Aug 2026
  // there's a real /services/home-maintenance-plans landing page (see
  // serviceData.js). The old /handyman-services/home-maintenance-plans
  // link will now resolve to that page instead of jumping straight to
  // /maintenance-membership, which is the correct behaviour for SEO
  // (the landing page pre-sells the concept and CTAs to membership).
  "/handyman-services/monthly-concierge-plan": "/maintenance-membership",
  "/handyman-services/compare-monthly-plans": "/maintenance-membership",
  // Bathroom/kitchen/interior remodels weren't rebuilt as standalone pages —
  // route to the umbrella remodeling page (which already exists in new app).
  "/handyman-services/bathroom-remodeling-services": "/services/remodeling-services",
  "/handyman-services/kitchen-remodeling-services": "/services/remodeling-services",
  "/handyman-services/interior-design-services": "/services/remodeling-services",
  "/handyman-services/commercial-maintenance-services": "/services",

  // Legacy CMS pages
  "/estimate": "/get-estimate",
  "/terms-conditions": "/terms",
  "/service-plan-terms-conditions": "/property-maintenance-terms",
  "/referral-reward-program": "/loyalty",

  // Pages that don't exist yet — sensible temporary destinations.
  // These get replaced when we ship /recent-handyman-work and /customer-reviews.
  "/gallery": "/recent-handyman-work",
  "/media-room": "/about",
  "/products": "/services",
};

// ---- 2. Pattern mappings ----
//   - Legacy blog posts: 22 known posts plus any future ones under category/.
//   - Old root-level posts (e.g., /tv-mounting-in-irvine) → /blog/<slug>.
const KNOWN_LEGACY_POSTS = new Set([
  "5-key-qualities-to-look-for-in-a-dependable-handyman-company",
  "backyard-bbq-build-in-newport-beach",
  "backyard-remodeling-service-in-newport-beach",
  "ceiling-finish-project-in-newport-beach",
  "dog-door-install-in-huntington-beach",
  "door-reconstruction-in-newport-beach",
  "doorway-conversion-service-in-newport-beach",
  "drywall-repair-in-newport-beach",
  "drywall-repair-in-newport-coast",
  "elevate-your-airbnb-rental-with-a-reliable-handyman-the-secret-to-enhanced-ratings-guest-satisfaction-and-increased-revenues",
  "elevating-rental-property-management-the-benefits-of-a-monthly-concierge-service-contract",
  "interior-design-in-newport-beach",
  "internet-doorbell-and-lock-installation-in-lido-isle-newport-beach",
  "picture-leveling-service-in-newport-beach",
  "shelf-installation-in-downtown-laguna-beach",
  "shelving-installation-at-fashion-island-in-newport-beach",
  "shower-tile-floor-in-newport-beach",
  "stucco-repair-in-tustin",
  "tile-repair-and-wood-corbel-replacement-in-newport-beach",
  "tv-installation-in-newport-beach",
  "tv-mounting-in-irvine",
  "unique-art-hanging-in-irvine",
]);

// ---- 3. Category pages → /blog (top of the funnel) ----
const CATEGORY_PREFIX = "/category/";

/**
 * Map a legacy URL to its new app equivalent. Returns null when the URL
 * is already a valid new-app path.
 */
export function mapLegacyPath(rawPathname) {
  let p = rawPathname || "/";

  // Rule 1: strip trailing slash (except root)
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }

  // Rule 2: exact mappings
  if (EXACT_REDIRECTS[p]) return EXACT_REDIRECTS[p];

  // Rule 3a: legacy blog post at root (e.g. /tv-mounting-in-irvine)
  if (/^\/[a-z0-9][a-z0-9-]+$/i.test(p)) {
    const slug = p.slice(1);
    if (KNOWN_LEGACY_POSTS.has(slug)) {
      return `/blog/${slug}`;
    }
  }

  // Rule 3b: WordPress category pages → /blog (top funnel)
  if (p.startsWith(CATEGORY_PREFIX)) {
    return "/blog";
  }

  // Rule 4: trailing-slash-only normalisation (caller handles)
  if (p !== rawPathname) return p;

  return null;
}

/**
 * Mounted at the catch-all route. Inspects the current location and
 * <Navigate replace> to the mapped target if any rule matches. Returns
 * null when no mapping found — the parent route renders <NotFound>.
 */
export default function SeoRedirects({ children }) {
  const loc = useLocation();
  const target = mapLegacyPath(loc.pathname);
  if (target && target !== loc.pathname) {
    return <Navigate to={target + (loc.search || "")} replace />;
  }
  return children || null;
}
